import type { IActiveLoan } from 'interfaces';
import type { AppDispatch } from 'store/store';
import { setExtensionErrorMessage } from 'store/slices/ui';
import { getApiErrorMessage, isLoanNotFoundError } from './apiErrors';
import { syncActiveLoanFromServer, type SyncActiveLoanResult } from './syncActiveLoan';

export const EXTENSION_BACKEND_MESSAGE = {
  NOT_REQUESTED: 'Extension is not requested',
  NOT_EXTENDABLE: 'Loan is not extendable',
  NO_ACTIVE_LOAN: 'Client has no active loan',
} as const;

export const EXTENSION_USER_MESSAGE = {
  NOT_REQUESTED: 'No se solicita la reprogramación del préstamo',
  NOT_EXTENDABLE: 'El préstamo no admite reprogramación',
} as const;

const CONFLICT_USER_MESSAGE_BY_BACKEND: Record<string, string> = {
  [EXTENSION_BACKEND_MESSAGE.NOT_REQUESTED]: EXTENSION_USER_MESSAGE.NOT_REQUESTED,
  [EXTENSION_BACKEND_MESSAGE.NOT_EXTENDABLE]: EXTENSION_USER_MESSAGE.NOT_EXTENDABLE,
};

type SyncLoanFn = (params: {
  dispatch: AppDispatch;
  partialView?: boolean;
  slug?: string;
}) => Promise<SyncActiveLoanResult>;

interface ResolveExtensionStaleErrorParams {
  dispatch: AppDispatch;
  error: unknown;
  partialView?: boolean;
  slug?: string;
  /** Override default loan sync (e.g. PTP flow with extra side effects). */
  syncLoan?: SyncLoanFn;
}

export const getExtensionConflictUserMessage = (
  backendMessage: string | undefined,
  loan: IActiveLoan | null | undefined,
): string | null => {
  if (!backendMessage || backendMessage === EXTENSION_BACKEND_MESSAGE.NO_ACTIVE_LOAN) {
    return null;
  }

  if (loan?.requested_extension != null) {
    return null;
  }

  return CONFLICT_USER_MESSAGE_BY_BACKEND[backendMessage] ?? null;
};

export const resolveExtensionStaleError = async ({
  dispatch,
  error,
  partialView = false,
  slug,
  syncLoan = syncActiveLoanFromServer,
}: ResolveExtensionStaleErrorParams): Promise<SyncActiveLoanResult | null> => {
  const backendMessage = getApiErrorMessage(error);
  let syncResult: SyncActiveLoanResult;

  try {
    syncResult = await syncLoan({ dispatch, partialView, slug });
  } catch (syncError) {
    console.log(syncError);
    return null;
  }

  if (
    syncResult.status === 'not_found' ||
    backendMessage === EXTENSION_BACKEND_MESSAGE.NO_ACTIVE_LOAN ||
    isLoanNotFoundError(error)
  ) {
    return syncResult;
  }

  const userMessage = getExtensionConflictUserMessage(
    backendMessage,
    syncResult.status === 'updated' ? syncResult.loan : null,
  );

  if (userMessage) {
    dispatch(setExtensionErrorMessage(userMessage));
  }

  return syncResult;
};
